import React, { useState, useEffect } from 'react';
import adminAPI from '../../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Database, Cpu, Wifi, AlertCircle, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

function StatusBadge({ status }) {
  const isOk = status === 'ok' || status === 'connected';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium ${
      isOk ? 'bg-[#34C759]/15 text-[#34C759]' : 'bg-[#FF9F0A]/15 text-[#FF9F0A]'
    }`}>
      {isOk ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
      {status}
    </span>
  );
}

export default function System() {
  const [health, setHealth] = useState(null);
  const [audit, setAudit] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [loadingAudit, setLoadingAudit] = useState(true);

  useEffect(() => { loadHealth(); }, []);
  useEffect(() => { loadAudit(); }, [auditPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHealth = async () => {
    setLoadingHealth(true);
    try {
      const data = await adminAPI.getHealth();
      setHealth(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingHealth(false);
    }
  };

  const loadAudit = async () => {
    setLoadingAudit(true);
    try {
      const data = await adminAPI.getAuditLog({ page: auditPage, limit: 20 });
      setAudit(data.logs || []);
      setAuditTotal(data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAudit(false);
    }
  };

  const auditPages = Math.ceil(auditTotal / 20) || 1;

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl text-white">System</h1>
          <p className="text-xs sm:text-sm text-white/40 mt-1">Helsestatus og revisjonslogg</p>
        </div>
        <button onClick={() => { loadHealth(); loadAudit(); }} className="h-10 px-4 rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white/50 flex items-center gap-2 hover:text-white hover:bg-white/5 transition-[background-color,color] duration-180">
          <RefreshCw className="w-4 h-4" /> Oppdater
        </button>
      </div>

      {/* Health */}
      <div data-testid="admin-system-health-cards" className="mb-8">
        <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">HELSESTATUS</p>
        {loadingHealth ? (
          <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" /></div>
        ) : health ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Wifi className="w-4 h-4 text-white/30" />
                <p className="text-xs text-white/40 uppercase tracking-wider">Status</p>
              </div>
              <StatusBadge status={health.status} />
            </div>
            <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-4 h-4 text-white/30" />
                <p className="text-xs text-white/40 uppercase tracking-wider">MongoDB</p>
              </div>
              <StatusBadge status={health.mongodb} />
            </div>
            <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Cpu className="w-4 h-4 text-white/30" />
                <p className="text-xs text-white/40 uppercase tracking-wider">LLM</p>
              </div>
              <StatusBadge status={health.llm} />
            </div>
            <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
              <p className="text-xs text-white/40 uppercase tracking-wider mb-3">DATABASE TELLERE</p>
              {health.counts && (
                <div className="space-y-1.5">
                  {Object.entries(health.counts).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between">
                      <span className="text-[10px] text-white/30 capitalize">{k.replace(/_/g, ' ')}</span>
                      <span className="text-sm text-white font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : <p className="text-sm text-white/30">Kunne ikke laste helsestatus</p>}
      </div>

      {/* Audit Log */}
      <div data-testid="admin-auditlog-list">
        <div className="flex items-center justify-between mb-4">
          <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40">REVISJONSLOGG</p>
          <span className="text-xs text-white/20">{auditTotal} poster</span>
        </div>
        <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 overflow-hidden">
          {loadingAudit ? (
            <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" /></div>
          ) : audit.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-white/30">Ingen poster i revisjonsloggen</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-white/30">Tidspunkt</th>
                      <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-white/30">Handling</th>
                      <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-white/30">Admin</th>
                      <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-white/30">Detaljer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((log, i) => (
                      <tr key={i} className="border-b border-white/[0.03]">
                        <td className="px-5 py-3 text-xs text-white/40">{log.timestamp ? new Date(log.timestamp).toLocaleString('nb-NO') : '—'}</td>
                        <td className="px-5 py-3 text-sm text-white/70">{log.action || '—'}</td>
                        <td className="px-5 py-3 text-xs text-white/40">{log.admin_email || log.admin_name || '—'}</td>
                        <td className="px-5 py-3 text-xs text-white/30 max-w-xs truncate">{log.details ? JSON.stringify(log.details) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {auditPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-white/5">
                  <p className="text-xs text-white/25">Side {auditPage}/{auditPages}</p>
                  <div className="flex gap-1">
                    <button disabled={auditPage <= 1} onClick={() => setAuditPage(p => p - 1)} className="p-1.5 rounded-lg text-white/30 hover:text-white disabled:opacity-20">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button disabled={auditPage >= auditPages} onClick={() => setAuditPage(p => p + 1)} className="p-1.5 rounded-lg text-white/30 hover:text-white disabled:opacity-20">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
